import React, { useEffect } from 'react';
import { useFormikContext } from 'formik';

import { policyDocumentCoverageNames } from '@ourbranch/lookups';
import { coverageRSLabelStates, noShowRoofSurfacesExtendedUnderXYearsStates } from '@ourbranch/roof-helpers';
import Grid from '@material-ui/core/Grid';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';

import { lockedValuesTooltipTexts } from 'core/helpers/constants';
import Field from 'core/components/form/form.v2';
import { LabelTooltip } from 'core/components/label-tooltip';
import { CoverageNameLabel } from 'common/components/coverage-name-label';
import { useToast } from 'core/components/toast';
import useOptions from 'core/hooks/use-options';
import useStyles from '../../coverages.styles';
import useExtendedRoofCoverage from '../../../hooks/roof-logic/use-extended-roof-coverage';

const ROOF_SURFACES_LABEL = 'Roof Surfaces';

const shouldCheckValue = (home, roofYearFocused, fromPolicy, lockedRSField, initialValues, stateOverride) => {
  if (stateOverride && home?.roofYear?.toString()?.length === 4) {
    return true;
  }

  return (
    home?.roofYear?.toString()?.length === 4 &&
    !roofYearFocused &&
    ((!fromPolicy && lockedRSField === true) || (fromPolicy && home.roofYear !== initialValues.home.roofYear))
  );
};

function RoofSurfacesCoverage({ id, state, fromPolicy, learnMoreRoofSurfaceTooltip }) {
  const classes = useStyles();
  const {
    values: { homeCoverage, home, ...values },
    initialValues,
    setFieldValue,
    setFieldTouched,
    touched
  } = useFormikContext();
  const toast = useToast();

  // we are manipulating touched onBlur and onFocus to know when we are focused on it, typically a field remains touched even on Blur
  const roofYearFocused = !!touched?.home?.roofYear || false;

  const roofSurfaceOptions = useOptions('roofSurfaceOptions', state, values?.[id]?.coverageRSExtended);

  const {
    lockedRSField,
    forcedScheduledRSCoverage,
    forcedExtendedRSCoverage,
    isCurrentValueCorrectValueInPolicy,
    policyBoundWithRoofExtendedSoWeHaveToHonorItIfRoofYearUnchanged,
    stateOverride
  } = useExtendedRoofCoverage(home, state, fromPolicy, initialValues, homeCoverage);

  useEffect(() => {
    // wait for user to finish typing year and get off of roofYear field
    if (shouldCheckValue(home, roofYearFocused, fromPolicy, lockedRSField, initialValues, stateOverride)) {
      let showToast;
      if (
        forcedScheduledRSCoverage &&
        (!fromPolicy ||
          noShowRoofSurfacesExtendedUnderXYearsStates[state] ||
          // in policy check if we originally bound with roof extended coverage, we have to honor that coverage even if roof year changes
          (fromPolicy &&
            !isCurrentValueCorrectValueInPolicy &&
            !policyBoundWithRoofExtendedSoWeHaveToHonorItIfRoofYearUnchanged))
      ) {
        if (homeCoverage?.coverageRSExtended) {
          showToast = true;
          setFieldValue(`${id}.coverageRSExtended`, false);
          setFieldTouched(`${id}.coverageRSExtended`, true, false);
        }
      }

      if (
        (!fromPolicy && forcedExtendedRSCoverage) ||
        (fromPolicy && noShowRoofSurfacesExtendedUnderXYearsStates[state] && forcedExtendedRSCoverage)
      ) {
        if (!homeCoverage?.coverageRSExtended) {
          showToast = true;
          setFieldValue(`${id}.coverageRSExtended`, true);
          setFieldTouched(`${id}.coverageRSExtended`, true, false);
        }
      }

      if (
        stateOverride &&
        //should be off but is on
        ((forcedScheduledRSCoverage && homeCoverage.coverageRSExtended) ||
          //should be on but is off
          (forcedExtendedRSCoverage && !homeCoverage.coverageRSExtended))
      ) {
        showToast = true;
        setFieldValue(`${id}.coverageRSExtended`, forcedExtendedRSCoverage);
        setFieldTouched(`${id}.coverageRSExtended`, true, false);
      }

      if (showToast) {
        toast.notify({
          type: 'success',
          message: 'Roof surfaces coverages automatically updated.',
          label: 'Learn More...  ',
          action: learnMoreRoofSurfaceTooltip.onClick
        });
      }
    }
  }, [forcedScheduledRSCoverage, forcedExtendedRSCoverage, lockedRSField, home.roofYear, roofYearFocused]);

  // States in coverageRSLabelStates print their own roof label on the documents (matching the
  // field label), so the note only appears where the documents say Roof Surfaces Extended Coverage.
  const showRoofDocumentNote = homeCoverage?.coverageRSExtended && !coverageRSLabelStates[state];
  const roofSurfacesLabel = showRoofDocumentNote ? (
    <CoverageNameLabel label={ROOF_SURFACES_LABEL} documentName={policyDocumentCoverageNames.roofSurfacesExtended} />
  ) : (
    ROOF_SURFACES_LABEL
  );

  return (
    <Grid item xs={6}>
      {lockedRSField ? (
        <Field
          id={`${id}.coverageRSExtended`}
          name={`${id}.coverageRSExtended`}
          label={roofSurfacesLabel}
          type="value"
          mode="locked"
          value={homeCoverage?.coverageRSExtended ? 'Replacement Cost (Extended)' : 'Standard (Payment Schedule)'}
          lockedValue={{
            text: homeCoverage?.coverageRSExtended
              ? lockedValuesTooltipTexts.roofSurfaceExtendedCoverage
              : lockedValuesTooltipTexts.roofSurfaceStandardCoverage
          }}
          showTooltip={learnMoreRoofSurfaceTooltip}
          className={classes.lockedRoofSurface}
        />
      ) : (
        <LabelTooltip label={roofSurfacesLabel} tooltip={learnMoreRoofSurfaceTooltip}>
          <Field
            id={`${id}.coverageRSExtended`}
            name={`${id}.coverageRSExtended`}
            type="select"
            mode="dark"
            options={roofSurfaceOptions}
          />
        </LabelTooltip>
      )}
    </Grid>
  );
}

RoofSurfacesCoverage.propTypes = {
  id: PropTypes.string.isRequired,
  state: PropTypes.string.isRequired,
  fromPolicy: PropTypes.bool.isRequired,
  learnMoreRoofSurfaceTooltip: PropTypes.object.isRequired
};

export default observer(RoofSurfacesCoverage);
